import {gql} from '@apollo/client';
import React, {useEffect} from 'react';

import * as ArchiveActions from 'archive/ArchiveActions';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import * as SourceActions from 'file-browser/SourceActions';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import licenses from 'user/Licenses';

import {
  ProjectSettingsPermissionsQuery,
  ProjectSettingsPermissionsQueryVariables,
} from './__generated/ProjectSettingsPermissionsQuery';
import SourceSettingsModal from './SourceSettingsModal';

export const PROJECT_SETTINGS_PERMISSIONS_QUERY = gql`
  query ProjectSettingsPermissionsQuery($sourceId: StrictString!) @skipAudit @engComponent(name: IDENTITY) {
    project(id: $sourceId) {
      ... on ProjectNode {
        id
        permissions {
          admin
          archivable
          unarchivable
          canManageCollaborators
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface ProjectSettingsModalProps {
  onDelete?: () => void;
  onUpdate?: () => void;
  onRequestClose: () => void;
  sourceId: string;
}

function useLoadingScreenWhile(isLoading: boolean): void {
  useEffect(() => {
    if (!isLoading) {
      return undefined;
    }
    LoadingScreen.show();
    return () => {
      LoadingScreen.hide();
    };
  }, [isLoading]);
}

function useCloseWhen(shouldClose: boolean, onRequestClose: () => void): void {
  useEffect(() => {
    if (shouldClose) {
      onRequestClose();
    }
  }, [shouldClose, onRequestClose]);
}

export function ProjectSettingsModal(props: ProjectSettingsModalProps): JSX.Element | null {
  const result = useBenchlingQuery<ProjectSettingsPermissionsQuery, ProjectSettingsPermissionsQueryVariables>(
    PROJECT_SETTINGS_PERMISSIONS_QUERY,
    {variables: {sourceId: props.sourceId}}
  );

  const isLoading = result.type === QueryResultType.PENDING;
  const project = result.type === QueryResultType.SUCCESS ? result.data.project : null;
  const cannotLoad = result.type === QueryResultType.ERROR || (project != null && isInaccessible(project));

  useLoadingScreenWhile(isLoading);
  useCloseWhen(cannotLoad, props.onRequestClose);

  if (project == null || isInaccessible(project)) {
    return null;
  }

  const {permissions} = project;
  const isReadOnly = !permissions.admin;
  const isArchivable = Boolean(permissions.archivable);
  const isUnarchiveable = Boolean(permissions.unarchivable);
  const canManageCollaborators = Boolean(permissions.canManageCollaborators);

  return (
    <SourceSettingsModal
      // @ts-expect-error
      archiveFn={isArchivable ? ArchiveActions.bulkArchiveSourcesWithDataMods : undefined}
      unarchiveFn={isUnarchiveable ? ArchiveActions.bulkUnarchiveSourcesWithDataMods : undefined}
      deleteFn={SourceActions.deleteSource}
      allowAuditorToggling={licenses.hasLicense('LICENSE_REVIEWS')}
      isReadOnly={isReadOnly}
      onDelete={props.onDelete}
      onUpdate={props.onUpdate}
      onRequestClose={props.onRequestClose}
      showCustomSettings={true}
      sourceId={props.sourceId}
      sourceType={ModelTypeConstants.PROJECT}
      canManageCollaborators={canManageCollaborators ?? false}
    />
  );
}
